<template>
  <div class="main-container">
    <div class="header">
      <div class="logo">生信帮</div>
      <div class="nav-links" :class="{ 'show': showMobileMenu }">
        <a href="#" v-for="nav in navLinks" :key="nav">{{ nav }}</a>
      </div>
      <button class="mobile-menu-btn" @click="showMobileMenu = !showMobileMenu">
        <span></span>
        <span></span>
        <span></span>
      </button>
      <div class="search-bar" :class="{ 'show-mobile': showMobileSearch }">
        <input type="text" :value="toolSearchKeyword" placeholder="搜索工具名称" @input="onSearchInput" @keyup.enter="handleToolSearch" />
        <button @click="handleToolSearch">
          搜索
        </button>
        <button class="mobile-search-close" @click="showMobileSearch = false">×</button>
      </div>
      <button class="mobile-search-toggle" @click="showMobileSearch = !showMobileSearch" :class="{ 'active': showMobileSearch }">
        <span>🔍</span>
      </button>
      <div class="user-area">
        <template v-if="isLoggedIn">
          <span>简体中文</span>
          <span>?</span>
          <span>🛒</span>
          <span>🔔</span>
          <div class="user-avatar" @click.stop="toggleUserMenu">
            <img :src="userAvatar" alt="User" @error="onAvatarError" />
            <div v-if="showUserMenu" class="user-dropdown">
              <div class="dropdown-item" @click="showChangePassword">
                <span>🔐</span> 修改密码
              </div>
              <div class="dropdown-divider"></div>
              <div class="dropdown-item logout" @click="logout">
                <span>🚪</span> 退出系统
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <button class="login-btn" @click="showLoginDialog = true">登录</button>
          <button class="register-btn" @click="showRegisterDialog = true">注册</button>
        </template>
      </div>
    </div>

    <div class="sidebar">
      <ul>
        <li v-for="menu in menuItems" :key="menu.name" @click="handleMenuClick(menu)">
          <i>{{ menu.icon }}</i> {{ menu.name }}
        </li>
      </ul>
    </div>

    <div class="main-content">
      <!-- 移动端顶部菜单栏 -->
      <div class="mobile-sidebar">
        <div class="mobile-menu-item" v-for="menu in menuItems" :key="menu.name" @click="handleMenuClick(menu)">
          <span class="menu-icon">{{ menu.icon }}</span>
          <span class="menu-name">{{ menu.name }}</span>
        </div>
      </div>

      <div v-if="!showDetail && !showHistory" class="tools-page">
        <div class="notice-bar">
          <span class="notice-icon">📢</span>
          <span class="notice-text">友情提醒：请点击右上角，扫码加微信，获取注册码，完成注册，可以保存分析结果3个月（注册码有限，先到先得）</span>
        </div>
        <div class="toolbar">
          <div class="tool-category">
            <span v-for="category in categories" :key="category" :class="{ active: activeCategory === category }"
              @click="selectCategory(category)">
              {{ category }}
            </span>
          </div>
          <div class="toolbar-right">
            <div v-if="isAdmin" class="action-menu-wrapper">
              <button class="action-menu-btn" @click.stop="toggleAdminActionMenu">
                <span>⚙️</span>
                <span>操作</span>
              </button>
              <div v-if="showAdminActionMenu" class="action-dropdown">
                <button class="action-item invite" @click="showAdminActionMenu = false; showInviteCodeDialog = true; loadInviteCodes()">
                  <span>🔑</span>
                  <span>邀请码管理</span>
                </button>
                <button class="action-item add" @click="showAdminActionMenu = false; goToAddTool()">
                  <span class="add-icon">+</span>
                  <span>新增工具</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="tool-card">
          <div v-if="loading" class="loading-container">
            <div class="spinner"></div>
            <p>加载中...</p>
          </div>
          <div v-else-if="filteredTools.length === 0" class="empty">
            <div class="empty-icon">🔍</div>
            <p>暂无数据</p>
          </div>
          <div class="card" v-for="tool in displayTools" :key="tool.id">
            <div class="card-header" @click="goToToolDetail(tool.id)">
              <img :src="toolImages[tool.id] || defaultToolImage" :alt="tool.title" loading="lazy" />
            </div>
            <div class="card-body" @click="goToToolDetail(tool.id)">
              <h3 class="card-title">{{ tool.title }}</h3>
              <p class="card-desc">{{ formatDescription(tool.description) }}</p>
              <div class="tag" v-for="tag in (tool.tags || [])" :key="tag">{{ tag }}</div>
              <div class="rating" v-if="tool.rating">
                <span>{{ tool.rating }}</span>
                <span class="heart">♡ {{ tool.likes }}</span>
              </div>
            </div>
            <div v-if="isAdmin" class="card-admin-bar">
              <button class="admin-btn edit" @click.stop="editTool(tool.id)">✏️ 编辑</button>
              <button class="admin-btn clone" @click.stop="cloneTool(tool.id)">📋 复制</button>
              <button class="admin-btn delete" @click.stop="deleteTool(tool.id)">🗑️ 删除</button>
            </div>
          </div>
          <div v-if="hasMoreTools" class="load-more" @click="loadMore">
            <span>点击加载更多</span>
          </div>
        </div>
      </div>

      <!-- 内嵌工具详情 -->
      <div v-if="showDetail" class="embedded-panel">
        <ToolDetail v-if="!isGeneFamilyTool" :id="selectedToolId" @back="closeDetail" @login-success="onLoginSuccess" />
        <GeneFamilyTool v-if="isGeneFamilyTool" :id="selectedToolId" @back="closeDetail" @login-success="onLoginSuccess" />
      </div>

      <!-- 内嵌历史任务 -->
      <div v-if="showHistory" class="embedded-panel">
        <HistoryTasks @back="closeHistory" />
      </div>

      <!-- 新增工具弹框 -->
      <div class="dialog-mask" v-if="showAddDialog">
        <div class="dialog">
          <h2>新增工具</h2>
          <label>封面图片</label>

          <div class="upload-box" @click="triggerUpload">

            <img v-if="form.image" :src="form.image" />

            <div v-else class="upload-placeholder">
              +
            </div>

            <input ref="fileInput" type="file" accept="image/*" style="display:none" @change="handleUpload" />

          </div>

          <div class="form-item">
            <label>标题</label>
            <input v-model="form.title" />
          </div>

          <div class="form-item">
            <label>描述</label>
            <textarea v-model="form.description"></textarea>
          </div>

          <div class="form-item">
            <label>所属分类</label>
            <div class="category-select-row">
              <select v-model="form.category" class="param-select" @change="onCategoryChange">
                <option v-for="cat in categoriesList" :key="cat" :value="cat">{{ cat }}</option>
                <option value="__new__">+ 新增分类</option>
              </select>
              <button v-if="form.category && form.category !== '__new__'" 
                      class="icon-btn delete" 
                      @click="removeCategory(form.category)" 
                      title="删除当前分类">🗑</button>
            </div>
            <div v-if="showCategoryInput" class="new-category-row">
              <input v-model="newCategoryName" type="text" placeholder="输入新分类名称" @keyup.enter="addCategory" />
              <button class="mini-btn" @click="addCategory">确定</button>
              <button class="mini-btn ghost" @click="showCategoryInput = false; newCategoryName = ''">取消</button>
            </div>
          </div>

          <div class="form-item">
            <label>标签</label>
            <div class="tag-input">
              <span class="tag-item" v-for="(tag, index) in form.tags" :key="index">
                {{ tag }}
                <b @click="removeTag(index)">×</b>
              </span>

              <input v-model="tagInput" @keyup.enter="addTag" placeholder="输入标签回车" />
            </div>
          </div>

          <div class="form-item">
            <label>评分</label>
            <div class="star-rating">
              <span v-for="i in 5" :key="i" @click="form.rating = i">
                {{ i <= form.rating ? '★' : '☆' }} </span>
            </div>
          </div>

          <div class="form-item">
            <label>收藏数量</label>
            <input type="number" v-model="form.likes" />
          </div>

          <div class="dialog-footer">
            <button class="ghost-btn" @click="showAddDialog = false">取消</button>
            <button class="add-btn" @click="submitTool">保存</button>
          </div>
        </div>
      </div>

      <!-- 修改密码弹框 -->
      <div class="dialog-mask" v-if="showPasswordDialog" @click.self="showPasswordDialog = false">
        <div class="dialog">
          <h2>修改密码</h2>
          
          <div class="form-item">
            <label>原密码</label>
            <input type="password" v-model="passwordForm.oldPassword" placeholder="请输入原密码" />
          </div>
          
          <div class="form-item">
            <label>新密码</label>
            <input type="password" v-model="passwordForm.newPassword" placeholder="请输入新密码（至少6位）" />
          </div>
          
          <div class="form-item">
            <label>确认新密码</label>
            <input type="password" v-model="passwordForm.confirmPassword" placeholder="请再次输入新密码" />
          </div>
          
          <div class="dialog-footer">
            <button class="ghost-btn" @click="showPasswordDialog = false">取消</button>
            <button class="add-btn" @click="submitChangePassword">确认修改</button>
          </div>
        </div>
      </div>

    </div>

    <!-- 登录弹窗 -->
    <LoginDialog :visible.sync="showLoginDialog" @success="onLoginSuccess" @switch-register="showRegisterDialog = true" />
    <!-- 注册弹窗 -->
    <RegisterDialog :visible.sync="showRegisterDialog" @switch-login="showLoginDialog = true" />

    <!-- 邀请码管理弹框 -->
    <div class="dialog-mask" v-if="showInviteCodeDialog" @click.self="showInviteCodeDialog = false">
      <div class="dialog invite-dialog">
        <div class="dialog-header">
          <h2>🔑 邀请码管理</h2>
          <button class="close-btn" @click="showInviteCodeDialog = false">×</button>
        </div>

        <div class="invite-toolbar">
          <button class="add-btn" @click="generateInviteCodes" :disabled="generating">
            {{ generating ? '生成中...' : '生成邀请码' }}
          </button>
          <span class="invite-tips">点击邀请码可复制，15位字母数字下划线组合</span>
        </div>

        <div v-if="inviteCodes.length === 0" class="empty">
          <div class="empty-icon">🔑</div>
          <p>暂无邀请码，点击上方按钮生成</p>
        </div>

        <div v-else class="invite-table-wrapper">
          <table class="invite-table">
            <thead>
              <tr>
                <th>邀请码</th>
                <th>状态</th>
                <th>使用者</th>
                <th>创建时间</th>
                <th>使用时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in inviteCodes" :key="item.id" :class="{ used: item.is_used }">
                <td>
                  <span class="code-text" :title="'点击复制'" @click="copyCode(item.code)">{{ item.code }}</span>
                </td>
                <td>
                  <span :class="['status-badge', item.is_used ? 'used-badge' : 'unused-badge']">
                    {{ item.is_used ? '已使用' : '未使用' }}
                  </span>
                </td>
                <td>{{ item.used_username || '-' }}</td>
                <td>{{ formatDate(item.created_at) }}</td>
                <td>{{ item.used_at ? formatDate(item.used_at) : '-' }}</td>
                <td>
                  <button class="icon-btn delete" @click="deleteInviteCode(item.id)">🗑</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getTools, getCategories, createCategoryApi, deleteCategoryApi,
  changePasswordApi, deleteToolApi, cloneToolApi,
  generateInviteCodesApi, getInviteCodesApi, deleteInviteCodeApi,
  getToolImage
} from '../api'
import { isLoginRequired } from '../utils/auth'
import ToolDetail from './ToolDetail.vue'
import GeneFamilyTool from './GeneFamilyTool.vue'
import HistoryTasks from './HistoryTasks.vue'
import LoginDialog from '../components/LoginDialog.vue'
import RegisterDialog from '../components/RegisterDialog.vue'

export default {
  name: 'Main',
  components: {
    ToolDetail,
    GeneFamilyTool,
    HistoryTasks,
    LoginDialog,
    RegisterDialog
  },
  data() {
    return {
      toolSearchKeyword: '',
      activeCategory: '全部',
      showMobileMenu: false,
      showMobileSearch: false,  // 移动端搜索栏展开状态
      // navLinks: ['云工具', '云流程', '云课堂', '云市场', '关于我们'],
      navLinks: [],
      menuItems: [
        { name: '绘图工具', icon: '🎨' },
        { name: '历史任务', icon: '📋' },
        // { name: '临床统计', icon: '📊' },
        // { name: '数据分析', icon: '📈' },
        // { name: '哨客岛', icon: '🏝️' },
        // { name: '我的云盘', icon: '📁' },
        // { name: '任务总览', icon: '📋' },
        // { name: '我的收藏', icon: '⭐' },
        // { name: '开发者工作台', icon: '🛠️' }
      ],
      categories: ['全部', '单细胞绘图', '动态交互', '关系图类', '面积图类', '散点图类', '折线图类', '柱状图类', '地图类', '科研工具', '其他', '组合图类', 'Shiny工具'],
      tools: [],
      toolImages: {},      // 工具封面图片缓存 { toolId: imageSrc }
      defaultToolImage: '/static/imgs/default_tool.jpg',
      loading: false,
      displayCount: 20,        // 分页：初始渲染数量
      searchDebounceTimer: null, // 搜索防抖定时器
      selectedToolId: null,   // 当前选中的工具
      showDetail: false,       // 是否显示详情页
      isGeneFamilyTool: false, // 是否为基因家族工具
      showHistory: false,      // 是否显示历史任务
      showAddDialog: false,
      showUserMenu: false,        // 用户下拉菜单
      showAdminActionMenu: false, // 管理员操作菜单
      showPasswordDialog: false,  // 修改密码弹框
      showLoginDialog: false,     // 登录弹框
      showRegisterDialog: false,  // 注册弹框
      showInviteCodeDialog: false, // 邀请码管理弹框
      inviteCodes: [],            // 邀请码列表
      generating: false,          // 生成中
      loginRedirect: null,        // 登录成功后跳转目标
      userAvatar: '',  // 用户头像
      isAdmin: false,  // 是否为管理员
      isLoggedIn: false, // 登录状态（响应式）
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },

      tagInput: '',
      categoriesList: [],
      newCategoryName: '',
      showCategoryInput: false,

      form: {
        title: '',
        description: '',
        tags: [],
        rating: 0,
        likes: 0,
        image: 'https://via.placeholder.com/350x150',
        category: '其他',
        tag: 'FREE',
        tagClass: 'free-tag',
        image: ''
      }

    }
  },
  computed: {
    filteredTools() {
      let result = this.tools

      // 合同系统和翻译系统仅 admin 可见
      if (!this.isAdmin) {
        const adminOnlyTools = ['合同系统', '翻译系统']
        result = result.filter(tool => !adminOnlyTools.includes(tool.title))
      }

      if (this.activeCategory !== '全部') {
        result = result.filter(tool => tool.category === this.activeCategory)
      }

      if (this.toolSearchKeyword) {
        const keyword = this.toolSearchKeyword.toLowerCase()
        result = result.filter(tool =>
          tool.title.toLowerCase().includes(keyword) ||
          tool.description.toLowerCase().includes(keyword)
        )
      }

      return result
    },
    displayTools() {
      return this.filteredTools.slice(0, this.displayCount)
    },
    hasMoreTools() {
      return this.filteredTools.length > this.displayCount
    }
  },
  watch: {
    displayTools: {
      handler() {
        this.loadToolImages()
      }
    }
  },
  created() {
    this.loadData()
    this.loadUserInfo()
    this.isLoggedIn = !!localStorage.getItem('token')
  },
  mounted() {
    // 点击外部关闭用户下拉菜单
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer)
    }
  },
  methods: {
    onAvatarError(e) {
      // 头像加载失败时使用本地默认头像兜底
      e.target.src = '/static/avatars/avatar1.svg'
    },
    handleClickOutside(e) {
      const userArea = this.$el.querySelector('.user-avatar')
      if (userArea && !userArea.contains(e.target)) {
        this.showUserMenu = false
      }
      const actionMenu = this.$el.querySelector('.action-menu-wrapper')
      if (actionMenu && !actionMenu.contains(e.target)) {
        this.showAdminActionMenu = false
      }
    },
    formatDescription(desc) {
      if (!desc) return ''
      // 首页工具列表中不显示由 AddTool 生成的“脚本: <路径>”前缀
      return desc.replace(/^脚本[：:]\s*.*/, '').trim()
    },
    async loadData() {
      this.loading = true
      try {
        const [toolsRes, categoriesRes] = await Promise.all([
          getTools(),
          getCategories()
        ])

        if (toolsRes.code === 200) {
          this.tools = toolsRes.data || []
          // 重置图片缓存，随后显式触发异步加载
          this.toolImages = {}
          this.loadToolImages()
        }

        if (categoriesRes.code === 200) {
          this.categoriesList = categoriesRes.data || []
          this.categories = ['全部', ...this.categoriesList]
        }
      } catch (error) {
        console.error('加载数据失败:', error)
        const errorMsg = error.response?.data?.message || error.message || '加载数据失败，请稍后重试'
        this.$toast.error(errorMsg)
      } finally {
        this.loading = false
      }
    },
    async loadToolImages() {
      // 仅对当前可见且未加载图片的工具加载
      const toolsToLoad = this.displayTools.filter(tool => !this.toolImages[tool.id])
      if (!toolsToLoad.length) return

      for (const tool of toolsToLoad) {
        // 兼容：若列表接口已返回图片（旧后端/base64/静态URL），直接复用，避免额外请求
        if (tool.image) {
          this.$set(this.toolImages, tool.id, tool.image)
          continue
        }

        try {
          const res = await getToolImage(tool.id)
          if (res.code === 200 && res.data && res.data.image) {
            this.$set(this.toolImages, tool.id, res.data.image)
          } else {
            this.$set(this.toolImages, tool.id, this.defaultToolImage)
          }
        } catch (error) {
          console.error(`加载工具 ${tool.id} 图片失败:`, error)
          this.$set(this.toolImages, tool.id, this.defaultToolImage)
        }
      }
    },

    loadUserInfo() {
      // 从 localStorage 加载用户信息
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      if (userInfo && userInfo.username) {
        let avatar = userInfo.avatar || '/static/avatars/avatar1.svg'
        // 旧的外部 DiceBear 头像统一替换成本地默认头像
        if (avatar && avatar.includes('dicebear.com')) {
          avatar = '/static/avatars/avatar1.svg'
        }
        this.userAvatar = avatar
        // 判断是否为管理员（admin 用户或 role 为 admin）
        this.isAdmin = userInfo.username === 'admin' || userInfo.role === 'admin'
      } else {
        // 未登录时清空用户信息
        this.userAvatar = '/static/avatars/avatar1.svg'
        this.isAdmin = false
      }
    },
    handleMenuClick(menu) {
      if (menu.name === '绘图工具') {
        this.closeDetail()
        this.closeHistory()
      } else if (menu.name === '历史任务') {
        if (!this.isLoggedIn) {
          this.$toast.warning('请先登录后再查看历史任务')
          this.showLoginDialog = true
          this.loginRedirect = 'history'
          return
        }
        this.closeDetail()
        this.showHistory = true
      }
    },
    goToAddTool() {
      this.$router.push('/add-tool');
    },
    onSearchInput(e) {
      const value = e.target.value
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.toolSearchKeyword = value
        this.displayCount = 20 // 重置分页
        if (!this.toolSearchKeyword.trim()) {
          this.activeCategory = '全部'
        }
      }, 300)
    },
    handleToolSearch() {
      // 点击搜索按钮时立即生效
      this.displayCount = 20
      if (!this.toolSearchKeyword.trim()) {
        this.activeCategory = '全部'
      }
    },
    selectCategory(category) {
      this.activeCategory = category
      this.toolSearchKeyword = ''
      this.displayCount = 20 // 切换分类时重置分页
    },
    loadMore() {
      this.displayCount += 20
    },
    goToToolDetail(toolId) {
      if (isLoginRequired() && !this.isLoggedIn) {
        this.$toast.warning('请先登录后再使用工具')
        this.showLoginDialog = true
        this.loginRedirect = 'tool:' + toolId
        return
      }
      this.closeHistory()
      this.selectedToolId = toolId
      this.isGeneFamilyTool = (toolId === 1 || toolId === '1')
      this.showDetail = true
    },
    closeDetail() {
      this.showDetail = false
      this.selectedToolId = null
      this.isGeneFamilyTool = false
    },
    closeHistory() {
      this.showHistory = false
    },
    onLoginSuccess() {
      this.isLoggedIn = true
      this.loadUserInfo()
      const redirect = this.loginRedirect
      this.loginRedirect = null
      if (redirect === 'history') {
        this.closeDetail()
        this.showHistory = true
      } else if (redirect && redirect.startsWith('tool:')) {
        const toolId = redirect.replace('tool:', '')
        this.closeHistory()
        this.selectedToolId = toolId
        this.isGeneFamilyTool = (toolId === '1')
        this.showDetail = true
      }
    },
    editTool(toolId) {
      this.$router.push(`/edit-tool/${toolId}`);
    },
    async cloneTool(toolId) {
      if (!confirm('确定要复制该工具吗？')) return;
      try {
        const res = await cloneToolApi(toolId);
        if (res.code === 200) {
          this.$toast.success('工具复制成功');
          await this.loadData();
        } else {
          this.$toast.error(res.message || '复制失败');
        }
      } catch (error) {
        const msg = error.response?.data?.message || '复制失败';
        this.$toast.error(msg);
      }
    },
    async deleteTool(toolId) {
      if (!confirm('确定要删除该工具吗？此操作不可恢复。')) return;
      try {
        const res = await deleteToolApi(toolId);
        if (res.code === 200) {
          this.$toast.success('工具已删除');
          this.tools = this.tools.filter(t => t.id !== toolId);
        } else {
          this.$toast.error(res.message || '删除失败');
        }
      } catch (error) {
        this.$toast.error('删除失败');
      }
    },
    openAddDialog() {
      this.showAddDialog = true
    },

    addTag() {
      if (!this.tagInput.trim()) return
      this.form.tags.push(this.tagInput)
      this.tagInput = ''
    },

    removeTag(index) {
      this.form.tags.splice(index, 1)
    },

    submitTool() {
      if (!this.form.title) {
        this.$toast.warning('请输入标题')
        return
      }

      const newTool = {
        ...this.form,
        id: Date.now()
      }

      this.tools.unshift(newTool)
      this.resetForm()
      this.showAddDialog = false
    },
    async addCategory() {
      const name = this.newCategoryName.trim()
      if (!name) {
        this.$toast.warning('请输入分类名称')
        return
      }
      try {
        const res = await createCategoryApi({ name })
        if (res.code === 200) {
          this.categoriesList.push(name)
          this.categories = ['全部', ...this.categoriesList]
          this.form.category = name
          this.newCategoryName = ''
          this.showCategoryInput = false
          this.$toast.success('分类创建成功')
        } else {
          this.$toast.error(res.message || '创建失败')
        }
      } catch (e) {
        this.$toast.error('创建分类失败')
      }
    },
    async removeCategory(name) {
      if (!confirm(`确定要删除分类 "${name}" 吗？`)) return
      try {
        const res = await deleteCategoryApi(name)
        if (res.code === 200) {
          this.categoriesList = this.categoriesList.filter(c => c !== name)
          this.categories = ['全部', ...this.categoriesList]
          if (this.form.category === name) {
            this.form.category = this.categoriesList[0] || '其他'
          }
          this.$toast.success('分类删除成功')
        } else {
          this.$toast.error(res.message || '删除失败')
        }
      } catch (e) {
        this.$toast.error('删除分类失败')
      }
    },
    onCategoryChange(e) {
      if (e.target.value === '__new__') {
        this.showCategoryInput = true
        this.form.category = this.categoriesList[0] || '其他'
      }
    },

    // 用户菜单相关方法
    toggleUserMenu() {
      this.showUserMenu = !this.showUserMenu
    },
    toggleAdminActionMenu() {
      this.showAdminActionMenu = !this.showAdminActionMenu
    },
    
    showChangePassword() {
      this.showUserMenu = false
      this.showPasswordDialog = true
      // 重置密码表单
      this.passwordForm = {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
    },
    
    async submitChangePassword() {
      // 表单验证
      if (!this.passwordForm.oldPassword || !this.passwordForm.newPassword || !this.passwordForm.confirmPassword) {
        this.$toast.warning('请填写完整信息')
        return
      }
      
      if (this.passwordForm.newPassword.length < 6) {
        this.$toast.warning('新密码长度不能少于6位')
        return
      }
      
      if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
        this.$toast.warning('两次输入的新密码不一致')
        return
      }
      
      try {
        // 获取当前用户名
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
        const username = userInfo.username || 'admin'
        
        const response = await changePasswordApi({
          username: username,
          old_password: this.passwordForm.oldPassword,
          new_password: this.passwordForm.newPassword
        })
        
        if (response.code === 200) {
          this.$toast.success('密码修改成功！请使用新密码重新登录')
          this.showPasswordDialog = false
          // 修改成功后退出登录
          this.logout()
        } else {
          this.$toast.error(response.message || '密码修改失败')
        }
      } catch (error) {
        console.error('Change password error:', error)
        const errorMsg = error.response?.data?.message || error.message || '密码修改失败'
        this.$toast.error(errorMsg)
      }
    },
    
    logout() {
      this.showUserMenu = false
      if (confirm('确定要退出系统吗？')) {
        // 清除登录信息
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        this.isLoggedIn = false
        this.userAvatar = ''
        this.isAdmin = false
        this.$toast.success('已退出系统')
      }
    },

    resetForm() {
      this.form = {
        title: '',
        description: '',
        tags: [],
        rating: 0,
        likes: 0,
        image: 'https://via.placeholder.com/350x150',
        category: this.categoriesList[0] || '其他',
        tag: 'FREE',
        tagClass: 'free-tag'
      }
      this.showCategoryInput = false
      this.newCategoryName = ''
    },
    triggerUpload() {
      this.$refs.fileInput.click()
    },

    async handleUpload(e) {

      const file = e.target.files[0]

      if (!file) return

      // 本地预览
      const reader = new FileReader()

      reader.onload = (res) => {
        this.form.image = res.target.result
      }

      reader.readAsDataURL(file)


      // ===== 上传回调 =====
      this.uploadImage(file)

    },


    // 上传图片接口
    async uploadImage(file) {

      const formData = new FormData()

      formData.append("file", file)

      try {

        const res = await this.$axios.post(
          "/api/upload",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          }
        )

        // 后端返回真实URL
        this.form.image = res.data.url

      } catch (e) {

        console.error("上传失败", e)

      }

    },

    // ============ 邀请码管理 ============
    async loadInviteCodes() {
      if (!this.isAdmin) return
      try {
        const res = await getInviteCodesApi()
        if (res.code === 200) {
          this.inviteCodes = res.data.list || []
        }
      } catch (e) {
        console.error('加载邀请码失败', e)
      }
    },

    async generateInviteCodes() {
      if (!this.isAdmin) return
      this.generating = true
      try {
        const res = await generateInviteCodesApi({ count: 1 })
        if (res.code === 200) {
          this.$toast.success(`成功生成 ${res.data.length} 个邀请码`)
          await this.loadInviteCodes()
        } else {
          this.$toast.error(res.message || '生成失败')
        }
      } catch (e) {
        const msg = e.response?.data?.message || e.message || '生成失败'
        this.$toast.error(msg)
      } finally {
        this.generating = false
      }
    },

    async deleteInviteCode(id) {
      if (!confirm('确定要删除该邀请码吗？')) return
      try {
        const res = await deleteInviteCodeApi(id)
        if (res.code === 200) {
          this.$toast.success('删除成功')
          this.inviteCodes = this.inviteCodes.filter(item => item.id !== id)
        } else {
          this.$toast.error(res.message || '删除失败')
        }
      } catch (e) {
        this.$toast.error('删除失败')
      }
    },

    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.$toast.success('邀请码已复制到剪贴板')
      } catch (e) {
        // 降级方案
        const input = document.createElement('input')
        input.value = code
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        this.$toast.success('邀请码已复制到剪贴板')
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return dateStr
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    }

  }
}
</script>

<style scoped>
.main-container {
  min-height: 100vh;
}

.notice-bar {
  background: linear-gradient(90deg, #fff7ed 0%, #ffedd5 100%);
  border: 1px solid #fdba74;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #9a3412;
  font-size: 14px;
  line-height: 1.5;
}

.notice-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.notice-text {
  flex: 1;
}

.header {
  background-color: #0066ff;
  color: white;
  padding: 0 20px;
  height: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 200;
}

.nav-links {
  display: flex;
  gap: 30px;
}

.nav-links a {
  color: white;
  text-decoration: none;
  font-size: 1.1rem;
}

.nav-links a:hover {
  text-decoration: underline;
}

.mobile-menu-btn {
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 30px;
  height: 25px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
}

.mobile-menu-btn span {
  width: 30px;
  height: 3px;
  background: white;
  border-radius: 3px;
  transition: all 0.3s ease;
}

.search-bar {
  display: flex;
  gap: 10px;
}

.search-bar input {
  padding: 8px;
  border-radius: 4px;
  border: none;
  width: 200px;
}

.search-bar button {
  background-color: #004d99;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.search-bar button:hover:not(:disabled) {
  background-color: #003366;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.search-bar button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.mobile-search-toggle {
  display: none;
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  transition: background 0.2s;
}

.mobile-search-toggle:hover {
  background: rgba(255, 255, 255, 0.15);
}

.mobile-search-toggle.active {
  background: rgba(255, 255, 255, 0.25);
}

.mobile-search-close {
  display: none;
  background: transparent;
  border: none;
  color: #0066ff;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  padding: 0 8px;
  margin-left: 4px;
}

.user-area {
  display: flex;
  gap: 15px;
  align-items: center;
}

.user-area img {
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.login-btn {
  background: transparent;
  color: white;
  border: 1px solid rgba(255,255,255,0.6);
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.login-btn:hover {
  background: rgba(255,255,255,0.15);
  border-color: white;
}

.register-btn {
  background: white;
  color: #0066ff;
  border: none;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.register-btn:hover {
  background: #f0f4ff;
  transform: translateY(-1px);
}

.category-select-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.category-select-row select {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
}

.new-category-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.new-category-row input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
}

.mini-btn {
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  transition: all 0.2s;
}

.mini-btn.ghost {
  background: #f1f5f9;
  color: #64748b;
}

.mini-btn:hover {
  transform: translateY(-1px);
}

.sidebar {
  width: 220px;
  background-color: #f0f2f5;
  height: calc(100vh - 60px);
  position: fixed;
  top: 60px;
  left: 0;
  padding: 20px 0;
  overflow-y: auto;
}

.sidebar ul {
  list-style: none;
}

.sidebar li {
  padding: 12px 20px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
}

.sidebar li:hover {
  background-color: #e6e8eb;
}

.sidebar i {
  margin-right: 8px;
}

.sidebar .admin-menu {
  background: linear-gradient(90deg, #ecfdf5 0%, #d1fae5 100%);
  color: #047857;
  font-weight: 600;
  border-left: 3px solid #10b981;
}

.sidebar .admin-menu:hover {
  background: linear-gradient(90deg, #d1fae5 0%, #a7f3d0 100%);
}

/* 移动端顶部菜单栏（桌面端隐藏） */
.mobile-sidebar {
  display: none;
}

.main-content {
  margin-left: 220px;
  margin-top: 6px;
}

.tools-page {
  padding: 20px;
}

.embedded-panel {
  height: calc(100vh - 60px);
  overflow: hidden;
}

/* 覆盖内嵌子组件的视口高度 */
.embedded-panel >>> .analysis-container,
.embedded-panel >>> .history-tasks-page {
  height: calc(100vh - 60px);
  min-height: calc(100vh - 60px);
}

.toolbar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.toolbar-right {
  display: flex;
  gap: 12px;
  align-items: center;
}

.toolbar-right input {
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  width: 200px;
  transition: all 0.3s ease;
}

.toolbar-right input:focus {
  outline: none;
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
}

.add-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.add-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);
}

.add-icon {
  font-size: 20px;
  font-weight: 300;
  line-height: 1;
}

.tool-category {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.tool-category span {
  padding: 6px 12px;
  background-color: #f0f2f5;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
}

.tool-category span:hover {
  background-color: #e6e8eb;
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tool-category span.active {
  background-color: #0066ff;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 102, 255, 0.3);
}

.tool-card {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.card {
  width: 350px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: all 0.3s ease;
  cursor: pointer;
}

.card:hover {
  transform: translateY(-8px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border-color: #0066ff;
}

.card img {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.card-body {
  padding: 15px;
}

.card-title {
  font-weight: bold;
  margin-bottom: 8px;
}

.card-desc {
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 12px;
}

.tag {
  background-color: #e6f7ff;
  color: #0066ff;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  margin-right: 8px;
}

.rating {
  display: flex;
  align-items: center;
  margin: 10px 0;
}

.rating span {
  color: gold;
  margin-right: 5px;
}

.heart {
  color: #ccc;
  cursor: pointer;
}

/* 卡片管理员操作栏 */
.card-admin-bar {
  display: flex;
  gap: 8px;
  padding: 10px 15px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
  border-radius: 0 0 8px 8px;
}

.admin-btn {
  flex: 1;
  padding: 6px 0;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
}

.admin-btn.edit {
  background: #e0f2fe;
  color: #0369a1;
}

.admin-btn.edit:hover {
  background: #bae6fd;
}

.admin-btn.delete {
  background: #fee2e2;
  color: #b91c1c;
}

.admin-btn.delete:hover {
  background: #fecaca;
}

.admin-btn.clone {
  background: #f0fdf4;
  color: #15803d;
}

.admin-btn.clone:hover {
  background: #bbf7d0;
}

.card-header {
  position: relative;
  overflow: hidden;
}

.free-tag {
  display: inline-block;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-top: 8px;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.3);
}

.vip-tag {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  position: absolute;
  top: 12px;
  right: 12px;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.35);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.member-discount {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  position: absolute;
  top: 12px;
  right: 12px;
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.35);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.loading-container {
  width: 100%;
  text-align: center;
  padding: 60px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3f;
  border-top: 4px solid #0066ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loading-container p {
  color: #666;
  font-size: 16px;
  margin: 0;
}

.empty {
  width: 100%;
  text-align: center;
  padding: 60px 40px;
  color: #999;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty p {
  font-size: 18px;
  margin: 0;
}

@media screen and (max-width: 1200px) {
  .card {
    width: calc(50% - 10px);
  }
}

@media screen and (max-width: 768px) {
  .header {
    padding: 10px;
    flex-wrap: wrap;
  }

  .mobile-menu-btn {
    display: flex;
  }

  .nav-links {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: #0052cc;
    flex-direction: column;
    padding: 20px;
    gap: 15px;
    z-index: 99;
  }

  .nav-links.show {
    display: flex;
  }

  .search-bar {
    order: 3;
    width: 100%;
    margin-top: 10px;
    display: none;
  }

  .search-bar.show-mobile {
    display: flex;
  }

  .search-bar input {
    width: 100%;
  }

  .mobile-search-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .mobile-search-close {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .user-area {
    margin-left: auto;
  }

  .sidebar {
    display: none;
  }

  .mobile-sidebar {
    display: flex;
    gap: 8px;
    padding: 10px 15px;
    background: #fff;
    border-bottom: 1px solid #e0e0e0;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .mobile-sidebar::-webkit-scrollbar {
    display: none;
  }

  .mobile-menu-item {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: #f0f2f5;
    border-radius: 20px;
    font-size: 14px;
    color: #333;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
  }

  .mobile-menu-item:active {
    background: #e6e8eb;
  }

  .mobile-menu-item .menu-icon {
    font-size: 16px;
  }

  .main-content {
    margin-left: 0;
  }

  .tools-page {
    padding: 15px;
  }

  .toolbar {
    flex-direction: column;
    gap: 15px;
  }

  .tool-category {
    flex-wrap: wrap;
    gap: 8px;
  }

  .tool-category span {
    font-size: 0.9rem;
    padding: 5px 10px;
  }

  .card {
    width: 100%;
  }
}

@media screen and (max-width: 480px) {
  .header {
    padding: 8px;
  }

  .logo {
    font-size: 1.5rem;
  }

  .user-area span {
    display: none;
  }

  .user-area img {
    display: block;
  }

  .tool-category span {
    font-size: 0.85rem;
    padding: 4px 8px;
  }
}

.primary-btn {
  background: #409eff;
  color: #fff;
  border: none;
  padding: 8px 18px;
  border-radius: 6px;
  cursor: pointer;
}

.dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.dialog {
  width: 500px;
  background: white;
  padding: 25px;
  border-radius: 8px;
}

.form-item {
  margin-bottom: 15px;
}

.form-item label {
  display: block;
  margin-bottom: 5px;
}

.form-item input,
.form-item textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.star-rating span {
  font-size: 24px;
  color: gold;
  cursor: pointer;
}

.tag-input {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.tag-item {
  background: #e6f7ff;
  padding: 4px 8px;
  border-radius: 4px;
}

.tag-item b {
  margin-left: 5px;
  cursor: pointer;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.ghost-btn {
  background: #fff;
  border: 1px solid #dcdfe6;
  padding: 8px 18px;
  border-radius: 6px;
  cursor: pointer;
}
.upload-box{

  width:200px;
  height:200px;

  border:2px dashed #d9d9d9;
  border-radius:8px;

  display:flex;
  align-items:center;
  justify-content:center;

  cursor:pointer;

  overflow:hidden;

  transition:all .2s;

}

.upload-box:hover{
  border-color:#409eff;
}

.upload-box img{

  width:100%;
  height:100%;
  object-fit:cover;

}

.upload-placeholder{

  font-size:60px;
  color:#bbb;

}

/* 用户头像下拉菜单 */
.user-avatar {
  position: relative;
  cursor: pointer;
}

.user-avatar img {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: transform 0.2s;
}

.user-avatar:hover img {
  transform: scale(1.1);
}

.user-dropdown {
  position: absolute;
  top: 45px;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 150px;
  z-index: 1000;
  overflow: hidden;
}

.dropdown-item {
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;
  font-size: 14px;
  color: #333;
}

.dropdown-item:hover {
  background-color: #f5f7fa;
}

.dropdown-item.logout {
  color: #f56c6c;
}

.dropdown-item.logout:hover {
  background-color: #fef0f0;
}

.dropdown-divider {
  height: 1px;
  background-color: #ebeef5;
  margin: 4px 0;
}

/* 点击外部关闭下拉菜单 */
.user-area {
  position: relative;
}

/* ============ 邀请码管理 ============ */
.action-menu-wrapper {
  position: relative;
}

.action-menu-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.action-menu-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.action-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 8px;
  min-width: 160px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-item {
  border: none;
  padding: 10px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  color: white;
  text-align: left;
  white-space: nowrap;
}

.action-item.invite {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.action-item.invite:hover {
  filter: brightness(1.05);
  transform: translateY(-1px);
}

.action-item.add {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.action-item.add:hover {
  filter: brightness(1.05);
  transform: translateY(-1px);
}

.add-icon {
  font-size: 20px;
  font-weight: 300;
  line-height: 1;
}

.invite-btn {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

.invite-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
}

.invite-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.invite-dialog {
  width: 720px;
  max-height: 80vh;
  overflow-y: auto;
}

.invite-dialog .dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.invite-dialog .dialog-header h2 {
  margin: 0;
  font-size: 18px;
  color: #1e293b;
}

.invite-dialog .close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #94a3b8;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.invite-dialog .close-btn:hover {
  background: #f1f5f9;
  color: #64748b;
}

.invite-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.invite-tips {
  color: #94a3b8;
  font-size: 13px;
}

.invite-table-wrapper {
  max-height: 55vh;
  overflow-y: auto;
}

.invite-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.invite-table th,
.invite-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.invite-table th {
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
  position: sticky;
  top: 0;
}

.invite-table tbody tr:hover {
  background: #f8fafc;
}

.invite-table tbody tr.used {
  opacity: 0.7;
}

.code-text {
  color: #0066ff;
  font-family: 'Courier New', monospace;
  font-weight: 600;
  cursor: pointer;
  user-select: all;
  background: #e6f7ff;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;
}

.code-text:hover {
  background: #bae6fd;
}

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.unused-badge {
  background: #d1fae5;
  color: #047857;
}

.used-badge {
  background: #e2e8f0;
  color: #64748b;
}

@media screen and (max-width: 768px) {
  .invite-dialog {
    width: 95%;
    padding: 16px;
  }

  .invite-table th,
  .invite-table td {
    padding: 8px;
    font-size: 12px;
  }
}

/* ============ 分页加载更多 ============ */
.load-more {
  width: 100%;
  text-align: center;
  padding: 16px;
  cursor: pointer;
  color: #0066ff;
  font-size: 14px;
  font-weight: 500;
  border: 1px dashed #c7d2fe;
  border-radius: 8px;
  margin-top: 10px;
  transition: all 0.3s ease;
  background: #eef2ff;
}

.load-more:hover {
  background: #e0e7ff;
  border-color: #0066ff;
}

/* 提升长列表滚动性能 */
.tool-card {
  content-visibility: auto;
  contain-intrinsic-size: 0 500px;
}
</style>
